import React, {useContext, useEffect, useState} from "react";
import TrackVASContext from "../../TrackVASContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {GenericTable} from "../generic/table/GenericTable";
import {defaultList} from "../generic/InitialStates";
import {ActionsColumnFormatter} from "../../column-formatters/ActionsColumnFormatter";
import {ENTITY_ID_TREATMENT, ENTITY_ID_DEALER} from "../generic/Constants";
import {showConfirmDialog} from "../../generic-components/ConfirmDialog";
import {genericDelete, genericGetAll} from "../generic/crud";
import {showToast, TOAST_SUCCESS} from "../../utils/toast/Toast";
import {TreatmentAddEditDialog} from "./TreatmentAddEditDialog";
import {TreatmentDetailsDialog} from "./TreatmentDetailsDialog";

export function TreatmentsPage() {
    const myData = useContext(TrackVASContext);
    const [reload, setReload] = useState(1);
    const [pageLoadComplete, setPageLoadComplete] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showModelId, setShowModelId] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedTreatmentId, setSelectedTreatmentId] = useState(null);
    const [dealers, setDealers] = useState([]);

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    // Custom formatter for clickable treatment name
    const TreatmentNameFormatter = (cellContent, row) => {
        return (
            <div>
                <a
                    href="#"
                    className="text-primary text-hover-primary fw-bold fs-6"
                    style={{
                        textDecoration: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'inline-block'
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        openDetailsDialog(row.id);
                    }}
                >
                    {row.name}
                </a>
                {row.description && (
                    <div className="text-muted fs-7 mt-1 d-none d-md-block">
                        {row.description}
                    </div>
                )}
            </div>
        );
    };

    // Custom formatter for category
    const CategoryFormatter = (cellContent, row) => {
        const badgeClass = row.category === 'HVT' ? 'badge-primary' : 'badge-info';
        return (
            <span className={`badge ${badgeClass}`} style={{ fontSize: '0.85rem', fontWeight: '500', padding: '5px 10px' }}>
                {row.category === 'HVT' ? 'High Value' : 'Low Value'}
            </span>
        );
    };

    // Custom formatter for pricing type
    const PricingTypeFormatter = (cellContent, row) => {
        const displayText = row.pricingType === 'SEGMENT_PRICING' ? 'Segment Pricing' : 'Job Type Pricing';
        return (
            <span className="text-gray-800">{displayText}</span>
        );
    };

    // Custom formatter for price range
    const PriceRangeFormatter = (cellContent, row) => {
        if (!row.pricing || row.pricing.length === 0) {
            return <span className="text-muted">N/A</span>;
        }

        const customerPrices = row.pricing.map(p => p.customerPrice).filter(price => price != null);

        if (customerPrices.length === 0) {
            return <span className="text-muted">N/A</span>;
        }

        const minPrice = Math.min(...customerPrices);
        const maxPrice = Math.max(...customerPrices);

        if (minPrice === maxPrice) {
            return (
                <span className="text-gray-800 fw-bold">
                    ₹{minPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            );
        }

        return (
            <div className="d-flex flex-column">
                <span className="text-gray-800 fw-bold">
                    ₹{minPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-muted" style={{ fontSize: '0.75rem' }}>to</span>
                <span className="text-gray-800 fw-bold">
                    ₹{maxPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            </div>
        );
    };

    // Custom formatter for VAS
    const VASFormatter = (cellContent, row) => {
        return row.isVAS ? (
            <span className="badge badge-success">VAS</span>
        ) : (
            <span className="badge badge-danger">NON VAS</span>
        );
    };

    // Custom formatter for Usage Count/Popularity
    const PopularityFormatter = (cellContent, row) => {
        const count = row.usageCount || 0;

        let badgeColor = 'badge-light-secondary';
        let iconClass = 'bi-star';
        let popularityText = 'New';
        let iconColor = 'text-secondary';

        if (count >= 50) {
            badgeColor = 'badge-light-danger';
            iconClass = 'bi-fire';
            popularityText = 'Hot';
            iconColor = 'text-danger';
        } else if (count >= 20) {
            badgeColor = 'badge-light-warning';
            iconClass = 'bi-star-fill';
            popularityText = 'Popular';
            iconColor = 'text-warning';
        } else if (count >= 5) {
            badgeColor = 'badge-light-primary';
            iconClass = 'bi-star-half';
            popularityText = 'Trending';
            iconColor = 'text-primary';
        } else if (count > 0) {
            badgeColor = 'badge-light-info';
            iconClass = 'bi-star';
            popularityText = 'Used';
            iconColor = 'text-info';
        }

        return (
            <div className="d-flex flex-column align-items-center" style={{ minWidth: '100px' }}>
                <span className={`badge ${badgeColor} mb-1`} style={{ fontSize: '0.75rem', fontWeight: '600', padding: '5px 10px' }}>
                    <i className={`bi ${iconClass} ${iconColor} me-1`}></i>
                    {popularityText}
                </span>
                <span className="text-gray-600 fw-bold" style={{ fontSize: '0.85rem' }}>
                    {count} {count === 1 ? 'use' : 'uses'}
                </span>
            </div>
        );
    };

    const columns = [
        {
            dataField: "id",
            text: "Id",
            hidden: true
        },
        {
            dataField: "name",
            text: "Treatment Name",
            formatter: TreatmentNameFormatter,
            search: true,
            style: {
                width: "400px"
            },
        },
        {
            dataField: "category",
            text: "Category",
            formatter: CategoryFormatter,
            showInMobile: false,
        },
        {
            dataField: "pricingType",
            text: "Pricing Type",
            formatter: PricingTypeFormatter,
            showInMobile: false,
        },
        {
            dataField: "pricing",
            text: "Price Range",
            formatter: PriceRangeFormatter,
            showInMobile: false,
            style: {
                width: "150px"
            },
        },
        {
            dataField: "dealerName",
            text: "Dealer",
        },
        {
            dataField: "isVAS",
            text: "VAS",
            formatter: VASFormatter,
            showInMobile: false,
        },
        {
            dataField: "usageCount",
            text: "Popularity",
            formatter: PopularityFormatter,
            sort: true,
            style: {
                width: "140px"
            },
        },
        {
            dataField: "action",
            text: "Actions",
            formatter: ActionsColumnFormatter,
            formatExtraData: {
                buttons: [
                    {
                        name: "Edit",
                        color: "primary",
                        icon: "edit",
                        method: openEditDialog
                    },
                    {
                        name: "Delete",
                        color: "danger",
                        icon: "trash-alt",
                        method: openDeleteDialog
                    }
                ],
            },
            classes: "text-right pr-0",
            headerClasses: "text-right pr-3",
            style: {
                minWidth: "100px",
            },
        },
    ];

    function openEditDialog(row) {
        setShowModelId(row.id);
        setShowModal(true);
    }

    function openDetailsDialog(treatmentId) {
        setSelectedTreatmentId(treatmentId);
        setShowDetailsModal(true);
    }

    function reloadData() {
        setReload(reload + 1);
    }

    async function openDeleteDialog(row) {
        const result = await showConfirmDialog("Do you really want to delete the Treatment? You will be able to delete it only if it is not used anywhere.");
        if (result) {
            genericDelete(ENTITY_ID_TREATMENT, row.id).then(() => {
                showToast("Treatment is Deleted!", TOAST_SUCCESS);
                reloadData();
            }).catch(() => {
            }).finally(() => {
                reloadData();
            });
        }
    }

    function addButtonClicked() {
        setShowModelId(null);
        setShowModal(true);
    }

    const handleClose = () => setShowModal(false);
    const handleDetailsClose = () => {
        setShowDetailsModal(false);
        setSelectedTreatmentId(null);
    };

    const handleEditFromDetails = (treatmentId) => {
        // Open the edit dialog with the treatment ID
        setShowModelId(treatmentId);
        setShowModal(true);
    };

    useEffect(() => {
        myData.setBaseData({
            toolBarButtons: [
                {
                    name: "ADD TREATMENT",
                    color: "primary",
                    icon: "fa fa-plus",
                    onClick: addButtonClicked,
                },
            ],
        });

        // Fetch dealers for the filter dropdown
        genericGetAll(ENTITY_ID_DEALER, {
            page: 1,
            sizePerPage: 1000, // Fetch all dealers
            currentIndex: 0
        }).then(({data}) => {
            if (data.entities) {
                const dealerOptions = data.entities.map(dealer => ({
                    id: dealer.id,
                    name: dealer.name
                }));
                setDealers(dealerOptions);
            }
        }).catch(() => {
            // Handle error silently or show toast
        });

        setPageLoadComplete(true);

        return () => {
            myData.setBaseData({
                toolBarButtons: []
            });
        };
    }, []);

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Treatments</PageTitle>
            <div className="card card-body">
                {pageLoadComplete && (
                    <GenericTable
                        reload={reload}
                        defaultData={defaultList}
                        columns={columns}
                        entityId={ENTITY_ID_TREATMENT}
                        filters={[
                            {
                                dataField: 'dealerId',
                                text: 'Dealer',
                                type: 'select',
                                options: dealers
                            }
                        ]}
                    />
                )}
            </div>
            <TreatmentAddEditDialog show={showModal} handleClose={handleClose} id={showModelId} onSave={reloadData}/>
            <TreatmentDetailsDialog
                show={showDetailsModal}
                handleClose={handleDetailsClose}
                treatmentId={selectedTreatmentId}
                onSave={reloadData}
                onEdit={handleEditFromDetails}
            />
        </>
    );
}
